import type { ExtractedColor } from "./types"

/**
 * Extrai todas as cores de um arquivo SVG
 * @param svgContent - O conteúdo do SVG como string
 * @returns Array de cores extraídas com contagem
 */
export function extractColorsFromSVG(svgContent: string): ExtractedColor[] {
  const colorMap = new Map<string, number>()

  // Regex patterns para encontrar cores
  const patterns = [
    // Cores hex (#RGB, #RRGGBB, #RRGGBBAA)
    /#([0-9a-fA-F]{3,8})\b/g,
    // rgb() e rgba()
    /rgb\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)/gi,
    /rgba\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*[\d.]+\s*\)/gi,
    // hsl() e hsla()
    /hsl\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})%?\s*,\s*(\d{1,3})%?\s*\)/gi,
    /hsla\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})%?\s*,\s*(\d{1,3})%?\s*,\s*[\d.]+\s*\)/gi,
    // Cores nomeadas comuns
    /(?:fill|stroke|color|stop-color|flood-color|lighting-color)\s*[:=]\s*["']?([a-zA-Z]+)["']?/gi,
  ]

  // Extrair cores hex
  const hexMatches = Array.from(svgContent.matchAll(patterns[0]))

for (const match of hexMatches) {
    const hex = normalizeHex(match[1])
    if (hex && isValidColor(hex)) {
      colorMap.set(hex, (colorMap.get(hex) || 0) + 1)
    }
  }

  // Extrair cores rgb
  const rgbPattern = /rgb\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)/gi
  let rgbMatch
  while ((rgbMatch = rgbPattern.exec(svgContent)) !== null) {
    const hex = rgbToHex(
      parseInt(rgbMatch[1]),
      parseInt(rgbMatch[2]),
      parseInt(rgbMatch[3])
    )
    if (hex) {
      colorMap.set(hex, (colorMap.get(hex) || 0) + 1)
    }
  }

  // Extrair cores rgba
  const rgbaPattern = /rgba\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*[\d.]+\s*\)/gi
  let rgbaMatch
  while ((rgbaMatch = rgbaPattern.exec(svgContent)) !== null) {
    const hex = rgbToHex(
      parseInt(rgbaMatch[1]),
      parseInt(rgbaMatch[2]),
      parseInt(rgbaMatch[3])
    )
    if (hex) {
      colorMap.set(hex, (colorMap.get(hex) || 0) + 1)
    }
  }

  // Extrair cores nomeadas em atributos
  const namedColorPattern = /(?:fill|stroke|color|stop-color|flood-color|lighting-color)\s*[:=]\s*["']?([a-zA-Z]+)["']?/gi
  let namedMatch
  while ((namedMatch = namedColorPattern.exec(svgContent)) !== null) {
    const colorName = namedMatch[1].toLowerCase()
    const hex = namedColorToHex(colorName)
    if (hex) {
      colorMap.set(hex, (colorMap.get(hex) || 0) + 1)
    }
  }

  // Converter para array e ordenar por contagem
  const colors: ExtractedColor[] = Array.from(colorMap.entries())
    .map(([hex, count]) => ({ hex, count }))
    .filter((color) => !isTransparentOrNone(color.hex))
    .sort((a, b) => b.count - a.count)

  return colors
}

/**
 * Normaliza um valor hex para formato #RRGGBB
 */
function normalizeHex(hex: string): string {
  hex = hex.toUpperCase()

  // Converter #RGB para #RRGGBB
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
  }

  // Remover alpha se presente (#RRGGBBAA -> #RRGGBB)
  if (hex.length === 8) {
    hex = hex.substring(0, 6)
  }

  return `#${hex}`
}

/**
 * Verifica se é uma cor válida
 */
function isValidColor(hex: string): boolean {
  return /^#[0-9A-F]{6}$/i.test(hex)
}

/**
 * Converte RGB para Hex
 */
function rgbToHex(r: number, g: number, b: number): string {
  if (r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255) {
    return ""
  }
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`
}

/**
 * Verifica se a cor é transparente ou none
 */
function isTransparentOrNone(hex: string): boolean {
  const transparent = ["none", "transparent", "#NONE", "#TRANSPARENT"]
  return transparent.includes(hex.toUpperCase())
}

/**
 * Converte nome de cor para hex
 */
function namedColorToHex(colorName: string): string | null {
  const colors: Record<string, string> = {
    black: "#000000",
    white: "#FFFFFF",
    red: "#FF0000",
    green: "#008000",
    blue: "#0000FF",
    yellow: "#FFFF00",
    cyan: "#00FFFF",
    magenta: "#FF00FF",
    gray: "#808080",
    grey: "#808080",
    silver: "#C0C0C0",
    maroon: "#800000",
    olive: "#808000",
    lime: "#00FF00",
    aqua: "#00FFFF",
    teal: "#008080",
    navy: "#000080",
    fuchsia: "#FF00FF",
    purple: "#800080",
    orange: "#FFA500",
    pink: "#FFC0CB",
    brown: "#A52A2A",
    gold: "#FFD700",
    coral: "#FF7F50",
    salmon: "#FA8072",
    turquoise: "#40E0D0",
    indigo: "#4B0082",
    violet: "#EE82EE",
    beige: "#F5F5DC",
    ivory: "#FFFFF0",
    khaki: "#F0E68C",
    lavender: "#E6E6FA",
    plum: "#DDA0DD",
    tan: "#D2B48C",
    wheat: "#F5DEB3",
    crimson: "#DC143C",
    darkblue: "#00008B",
    darkgreen: "#006400",
    darkred: "#8B0000",
    lightblue: "#ADD8E6",
    lightgreen: "#90EE90",
    lightgray: "#D3D3D3",
    lightgrey: "#D3D3D3",
    darkgray: "#A9A9A9",
    darkgrey: "#A9A9A9",
  }

  return colors[colorName] || null
}

/**
 * Gera um ID único
 */
export function generateId(): string {
  return `ct_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

/**
 * Calcula a luminosidade de uma cor para determinar se o texto deve ser claro ou escuro
 */
export function getContrastColor(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

  return luminance > 0.5 ? "#000000" : "#FFFFFF"
}
